import { InputText } from "primereact/inputtext";
import classes from "../../RuleWizard/RuleWizard.module.css";
import { OptionCard, StepHeader, StepNav } from "./AiboxWizardParts";

// Selection keys are stored as the exact strings the backend expects in zoneConfig.ruleUUIDs:
//   channel      -> "<channel no>"
//   channel_rule -> "<rule uuid>"  (a rule can be shared by several channels - listed once)
//   rule         -> "<system rule uuid>"
//   output_relay -> "<relay_id>"
export const CONTROL_LEVELS = [
    { key: "hub", label: "Hub", description: "Arm/disarm the whole AiBox" },
    { key: "channel", label: "Channels", description: "Arm/disarm individual channels" },
    { key: "channel_rule", label: "Channel rules", description: "Enable/disable rules on specific channels" },
    { key: "rule", label: "System rules", description: "Enable/disable system-wide rules" },
    { key: "output_relay", label: "Relays", description: "Control relay outputs" },
];

export const getControlLevelLabel = (key) => CONTROL_LEVELS.find(l => l.key === key)?.label ?? key;

// "channel_rule" is UI-only (to list channel rules separately from system rules) -
// the backend treats both as "rule".
export const buildZoneConfig = ({ controlLevel, selectedControlKeys, zoneName }) => [
    {
        zoneName,
        ruleUUIDs: controlLevel === "hub" ? [] : selectedControlKeys,
        controlLevel: controlLevel === "channel_rule" ? "rule" : controlLevel,
    },
];

// Channel rules are shared across channels, so collapse them to one entry per uuid
// and record which channels use each one.
const getUniqueChannelRules = (channels) => {
    const byUUID = new Map();
    channels.forEach(ch => {
        (ch.rules ?? []).forEach(rule => {
            if (!byUUID.has(rule.uuid)) byUUID.set(rule.uuid, { ...rule, channels: [] });
            byUUID.get(rule.uuid).channels.push(ch);
        });
    });
    return [...byUUID.values()];
};

const ChannelChips = ({ channels }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "6px" }}>
        {channels.map(ch => (
            <span key={ch.no} style={{
                fontSize: "11px", padding: "1px 8px", borderRadius: "10px",
                background: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9",
            }}>
                {ch.name}
            </span>
        ))}
    </div>
);

const getLevelCount = (key, { channels, rules, relays }) => {
    switch (key) {
        case "channel": return channels.length;
        case "channel_rule": return getUniqueChannelRules(channels).length;
        case "rule": return rules.length;
        case "output_relay": return relays.length;
        default: return null;
    }
};

const EnabledPill = ({ enabled }) => (
    <span style={{
        fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "10px", flexShrink: 0,
        background: enabled ? "#e8f5e9" : "#f5f5f5",
        color: enabled ? "#2e7d32" : "#9e9e9e",
        border: `1px solid ${enabled ? "#a5d6a7" : "#e0e0e0"}`,
    }}>
        {enabled ? "Enabled" : "Disabled"}
    </span>
);

const EmptyText = ({ children }) => (
    <p style={{ color: "var(--text-label)", fontSize: "13px", textAlign: "center", marginTop: "2rem" }}>{children}</p>
);

const AiboxStepControlType = ({
    channels, rules, relays,
    controlLevel, setControlLevel,
    selectedControlKeys, setSelectedControlKeys,
    zoneName, setZoneName,
    onNext, onBack, isLastStep,
}) => {
    const data = { channels, rules, relays };

    const selectLevel = (key) => {
        if (key === controlLevel) return;
        setControlLevel(key);
        setSelectedControlKeys([]);
    };

    const toggleKey = (key) => {
        setSelectedControlKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const renderSelectable = () => {
        switch (controlLevel) {
            case null:
                return <EmptyText>Choose what you'd like to control on the left.</EmptyText>;
            case "hub":
                return <EmptyText>The whole AiBox will be armed and disarmed together - nothing else to select.</EmptyText>;
            case "channel":
                if (channels.length === 0) return <EmptyText>No channels were found on this AiBox.</EmptyText>;
                return channels.map(ch => (
                    <OptionCard
                        key={ch.no}
                        title={ch.name}
                        subtitle={`Channel ${ch.no} · ${ch.status ?? "unknown"}`}
                        selected={selectedControlKeys.includes(String(ch.no))}
                        onClick={() => toggleKey(String(ch.no))}
                    />
                ));
            case "channel_rule": {
                const channelRules = getUniqueChannelRules(channels);
                if (channelRules.length === 0) return <EmptyText>No channel rules were found on this AiBox.</EmptyText>;
                return channelRules.map(rule => {
                    const key = String(rule.uuid);
                    return (
                        <OptionCard
                            key={key}
                            top={<ChannelChips channels={rule.channels} />}
                            title={rule.name}
                            subtitle={rule.type}
                            selected={selectedControlKeys.includes(key)}
                            onClick={() => toggleKey(key)}
                            right={<EnabledPill enabled={rule.enabled} />}
                        />
                    );
                });
            }
            case "rule":
                if (rules.length === 0) return <EmptyText>No system rules were found on this AiBox.</EmptyText>;
                return rules.map(rule => (
                    <OptionCard
                        key={rule.uuid}
                        title={rule.name}
                        subtitle={rule.type}
                        selected={selectedControlKeys.includes(String(rule.uuid))}
                        onClick={() => toggleKey(String(rule.uuid))}
                        right={<EnabledPill enabled={rule.enabled} />}
                    />
                ));
            case "output_relay":
                if (relays.length === 0) return <EmptyText>No relays were found on this AiBox.</EmptyText>;
                return relays.map(relay => (
                    <OptionCard
                        key={relay.relay_id}
                        title={relay.name}
                        subtitle={`Relay ${relay.relay_id}`}
                        selected={selectedControlKeys.includes(String(relay.relay_id))}
                        onClick={() => toggleKey(String(relay.relay_id))}
                        right={<EnabledPill enabled={relay.enabled} />}
                    />
                ));
            default:
                return null;
        }
    };

    const needsSelection = controlLevel && controlLevel !== "hub";
    const canContinue = controlLevel === "hub" || (needsSelection && selectedControlKeys.length > 0);

    return (
        <>
            <StepHeader title="What would you like to control?" subtitle="Pick one control type, then choose what it applies to" />

            <div className={classes.actionsLayout}>
                <div className={classes.actionsPinList} style={{ width: "210px", gap: "8px" }}>
                    {CONTROL_LEVELS.map(level => {
                        const count = getLevelCount(level.key, data);
                        return (
                            <OptionCard
                                key={level.key}
                                radio
                                title={count == null ? level.label : `${level.label} (${count})`}
                                subtitle={level.description}
                                selected={controlLevel === level.key}
                                disabled={count === 0}
                                onClick={() => selectLevel(level.key)}
                            />
                        );
                    })}
                </div>

                <div className={classes.actionsConfig} style={{ gap: "8px" }}>
                    {renderSelectable()}
                </div>
            </div>

            {needsSelection && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", paddingTop: "12px", flexShrink: 0 }}>
                    <label htmlFor="aiboxZoneName" style={{ fontSize: "13px", fontWeight: 600, color: "#2d2d2d" }}>Zone name</label>
                    <InputText id="aiboxZoneName" value={zoneName} onChange={(e) => setZoneName(e.target.value)} placeholder="e.g. Front yard" />
                    <small style={{ color: "#9e9e9e" }}>Set a name for the zone being controlled.</small>
                </div>
            )}

            <StepNav
                onBack={onBack}
                onNext={onNext}
                nextDisabled={!canContinue}
                nextLabel={isLastStep ? "Link AiBox" : "Next"}
                nextIcon={isLastStep ? "pi pi-link" : "pi pi-arrow-right"}
            />
        </>
    );
};

export default AiboxStepControlType;
